import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import { useAuth } from "../../contexts/AuthContext";
import {
  clusters as clustersAPI,
  teams as teamsAPI,
  customers as customersAPI,
  users as usersAPI,
  surveys as surveysAPI,
  staffAssignments as staffAssignmentsAPI,
} from "../../lib/database";
import { supabase } from "../../lib/supabase";
import { ChevronDown, Check } from "lucide-react";
import { hashPassword } from "../../lib/password";
import Icon from "../../components/AppIcon";
import Select from "../../components/ui/Select";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";

const ClusterDashboard = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [cluster, setCluster] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState(new Set());
  const [stats, setStats] = useState({
    totalCustomers: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
  });
  const [activeTab, setActiveTab] = useState("village"); // village | teams | members
  const [selectedTeamFilter, setSelectedTeamFilter] = useState("all");

  const [phaseTab, setPhaseTab] = useState("survey"); // survey | civil | technical
  const [villageDropdownOpen, setVillageDropdownOpen] = useState(false);
  const [villageSearch, setVillageSearch] = useState("");
  const [selectedVillages, setSelectedVillages] = useState([]);
  const villageDropdownRef = useRef(null);

  // Team management state
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamForm, setTeamForm] = useState({
    id: null,
    name: "",
    user_ids: [],
  });
  const [teamFormErrors, setTeamFormErrors] = useState({});
  const [teamFormLoading, setTeamFormLoading] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState(null);

  // Team member management state
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberForm, setMemberForm] = useState({
    name: "",
    role: "",
    team_id: "",
  });
  const [memberFormErrors, setMemberFormErrors] = useState({});
  const [memberFormLoading, setMemberFormLoading] = useState(false);
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [newUserUniqueId, setNewUserUniqueId] = useState(null);

  useEffect(() => {
    fetchClusterData();
  }, [user]);

  // Fetch all customers (Supabase defaults can cap results)
  const fetchAllCustomers = async (filters = {}) => {
    const allCustomers = [];
    let page = 1;
    const pageSize = 1000;
    let hasMore = true;

    while (hasMore) {
      // customersAPI.getAll supports pagination via { page, pageSize, filters }
      // eslint-disable-next-line no-await-in-loop
      const pageCustomers = await customersAPI
        .getAll({ page, pageSize, filters })
        .catch(() => []);

      if (pageCustomers && pageCustomers.length > 0) {
        allCustomers.push(...pageCustomers);
        if (pageCustomers.length < pageSize) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }

    return allCustomers;
  };

  const fetchClusterData = async () => {
    try {
      setLoading(true);

      if (!user?.cluster_id) {
        setLoading(false);
        return;
      }

      const clusterId = user.cluster_id;
      const baseFilters = { cluster_id: clusterId };

      // Fetch cluster details
      const clusterData = await clustersAPI.getById(clusterId);
      setCluster(clusterData);

      // Fetch teams in this cluster
      const teamsData = await teamsAPI.getByClusterId(clusterId);
      setTeams(teamsData || []);

      // Fetch team members
      const membersData = await usersAPI.getByClusterId(clusterId);
      setTeamMembers(membersData || []);

      // Fetch customers in this cluster (for display purposes, with pagination)
      const customersData = await fetchAllCustomers(baseFilters);
      setCustomers(customersData || []);

      // Fetch minimal datasets for village analytics
      const [
        surveysData,
        surveyAssignments,
        civilAssignments,
        technicalAssignments,
        commissioningAssignments,
      ] = await Promise.all([
        surveysAPI.getDashboardMinimal(baseFilters).catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "surveyor" })
          .catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "civil" })
          .catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "technical" })
          .catch(() => []),
        staffAssignmentsAPI
          .getDashboardMinimal({ ...baseFilters, user_role: "commissioning" })
          .catch(() => []),
      ]);

      setSurveys(surveysData || []);
      setAssignments([
        ...(surveyAssignments || []),
        ...(civilAssignments || []),
        ...(technicalAssignments || []),
        ...(commissioningAssignments || []),
      ]);

      // Use count queries for accurate statistics (not limited by pagination)
      const [
        totalCount,
        surveyNotStartedCount,
        surveyInProgressCount,
        surveyCompleteCount,
        civilPendingCount,
        civilInProgressCount,
        civilCompleteCount,
        technicalPendingCount,
        technicalInProgressCount,
        pipelineCompleteCount,
        commissioningNotStartedCount,
        commissioningInProgressCount,
      ] = await Promise.all([
        customersAPI.getCount(baseFilters),
        customersAPI.getCount({ ...baseFilters, status: "survey_not_started" }),
        customersAPI.getCount({ ...baseFilters, status: "survey_inprogress" }),
        customersAPI.getCount({ ...baseFilters, status: "survey_complete" }),
        customersAPI.getCount({ ...baseFilters, status: "civil_pending" }),
        customersAPI.getCount({ ...baseFilters, status: "civil_inprogress" }),
        customersAPI.getCount({ ...baseFilters, status: "civil_complete" }),
        customersAPI.getCount({ ...baseFilters, status: "technical_pending" }),
        customersAPI.getCount({
          ...baseFilters,
          status: "technical_inprogress",
        }),
        customersAPI.getCount({
          ...baseFilters,
          status: ["technical_complete", "commissioning_complete"],
        }),
        customersAPI.getCount({
          ...baseFilters,
          status: "commissioning_not_started",
        }),
        customersAPI.getCount({
          ...baseFilters,
          status: "commissioning_inprogress",
        }),
      ]);

      // Calculate stats from accurate counts
      const total = totalCount || 0;
      const pending =
        surveyNotStartedCount +
          civilPendingCount +
          technicalPendingCount +
          commissioningNotStartedCount || 0;
      const inProgress =
        surveyInProgressCount +
          civilInProgressCount +
          technicalInProgressCount +
          commissioningInProgressCount || 0;
      const completed = pipelineCompleteCount || 0;

      setStats({ totalCustomers: total, pending, inProgress, completed });
    } catch (error) {
      console.error("Error fetching cluster data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate random password
  const generateRandomPassword = () => {
    const length = 8;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // Generate unique_id based on role
  const generateUniqueId = async (role) => {
    const { data, error } = await supabase.rpc("generate_unique_user_id", {
      role_type: role,
    });
    if (error) {
      throw error;
    }
    return data;
  };

  // Team CRUD handlers
  const validateTeamForm = () => {
    const newErrors = {};
    if (!teamForm?.name?.trim()) {
      newErrors.name = "Team name is required";
    }
    setTeamFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTeamSubmit = async (e) => {
    e?.preventDefault();
    if (!validateTeamForm()) return;

    try {
      setTeamFormLoading(true);
      const clusterId = user?.cluster_id;

      // Create or update team
      const team = teamForm.id
        ? await teamsAPI.update(teamForm.id, {
            name: teamForm.name,
            cluster_id: clusterId,
          })
        : await teamsAPI.create({
            name: teamForm.name,
            cluster_id: clusterId,
          });

      // Update users' team_id
      const previousMembers = teamMembers.filter(
        (member) => member.team_id === team.id,
      );
      const previousMemberIds = previousMembers.map((m) => m.id);
      const removedMemberIds = previousMemberIds.filter(
        (memberId) => !teamForm.user_ids.includes(memberId),
      );

      for (const userId of removedMemberIds) {
        await usersAPI.update(userId, { team_id: null });
      }

      for (const userId of teamForm.user_ids || []) {
        await usersAPI.update(userId, { team_id: team.id });
      }

      showToast(
        teamForm.id
          ? "Team updated successfully!"
          : "Team created successfully!",
        "success",
      );
      setTeamForm({ id: null, name: "", user_ids: [] });
      setTeamFormErrors({});
      setIsAddingTeam(false);
      setEditingTeamId(null);
      await fetchClusterData();
    } catch (err) {
      console.error("Error saving team:", err);
      showToast("Failed to save team. Please try again.", "error");
    } finally {
      setTeamFormLoading(false);
    }
  };

  const handleEditTeam = (team) => {
    const membersForTeam = teamMembers.filter(
      (member) => member.team_id === team.id,
    );
    setTeamForm({
      id: team.id,
      name: team.name || "",
      user_ids: membersForTeam.map((member) => member.id),
    });
    setEditingTeamId(team.id);
    setIsAddingTeam(false);
  };

  const handleDeleteTeam = async (teamId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this team? This will remove all team memberships.",
      )
    ) {
      try {
        setDeletingTeamId(teamId);
        await teamsAPI.delete(teamId);
        showToast("Team deleted successfully!", "success");
        await fetchClusterData();
      } catch (err) {
        console.error("Error deleting team:", err);
        showToast("Failed to delete team. Please try again.", "error");
      } finally {
        setDeletingTeamId(null);
      }
    }
  };

  const handleCancelTeam = () => {
    setIsAddingTeam(false);
    setEditingTeamId(null);
    setTeamForm({ id: null, name: "", user_ids: [] });
    setTeamFormErrors({});
  };

  // Team member CRUD handlers
  const roleOptions = [
    { value: "worker", label: "Worker" },
  ];

  const validateMemberForm = () => {
    const newErrors = {};
    if (!memberForm?.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!memberForm?.role) {
      newErrors.role = "Role is required";
    }
    setMemberFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMemberSubmit = async (e) => {
    e?.preventDefault();
    if (!validateMemberForm()) return;

    try {
      setMemberFormLoading(true);
      const clusterId = user?.cluster_id;
      const normalizedTeamId =
        memberForm.team_id && memberForm.team_id.trim() !== ""
          ? memberForm.team_id
          : null;

      if (editingMemberId) {
        // Update existing member
        await usersAPI.update(editingMemberId, {
          name: memberForm.name,
          cluster_id: clusterId,
          role: memberForm.role,
          team_id: normalizedTeamId,
        });
        showToast("Team member updated successfully!", "success");
        setEditingMemberId(null);
      } else {
        // Create new member
        const password = generateRandomPassword();
        const uniqueId = await generateUniqueId(memberForm.role);

        await usersAPI.create({
          unique_id: uniqueId,
          password: hashPassword(password),
          role: memberForm.role,
          category: "staff",
          name: memberForm.name,
          cluster_id: clusterId,
          team_id: normalizedTeamId,
          project_id: cluster?.project_id || null,
        });

        setGeneratedPassword(password);
        setNewUserUniqueId(uniqueId);
        setIsAddingMember(false);
      }

      setMemberForm({ name: "", role: "", team_id: "" });
      setMemberFormErrors({});
      await fetchClusterData();
    } catch (err) {
      console.error("Error saving team member:", err);
      showToast("Failed to save team member. Please try again.", "error");
    } finally {
      setMemberFormLoading(false);
    }
  };

  const handleEditMember = (member) => {
    setMemberForm({
      name: member.name || "",
      role: member.role || "",
      team_id: member.team_id || "",
    });
    setEditingMemberId(member.id);
    setIsAddingMember(false);
  };

  const handleDeleteMember = async (memberId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this team member? This action cannot be undone.",
      )
    ) {
      try {
        setDeletingMemberId(memberId);
        await usersAPI.delete(memberId);
        showToast("Team member deleted successfully!", "success");
        await fetchClusterData();
      } catch (err) {
        console.error("Error deleting team member:", err);
        showToast("Failed to delete team member. Please try again.", "error");
      } finally {
        setDeletingMemberId(null);
      }
    }
  };

  const handleCancelMember = () => {
    setIsAddingMember(false);
    setEditingMemberId(null);
    setMemberForm({ name: "", role: "", team_id: "" });
    setMemberFormErrors({});
  };

  const handleClearPassword = () => {
    setGeneratedPassword(null);
    setNewUserUniqueId(null);
  };

  // Get team options for member form
  const teamOptions =
    teams?.map((team) => ({
      value: team.id,
      label: team.name,
    })) || [];

  // Get user options for team form
  const userOptions =
    teamMembers
      ?.filter((member) => member.category === "staff")
      ?.map((member) => ({
        value: member.id,
        label: `${member.unique_id} - ${member.name || "Unnamed"}`,
      })) || [];

  const normalizeVillage = (v) => {
    const s = (v || "").toString().trim();
    return s ? s : "—";
  };

  const isSameDay = (iso, day) => {
    if (!iso) return false;
    const d = new Date(iso);
    return (
      d.getFullYear() === day.getFullYear() &&
      d.getMonth() === day.getMonth() &&
      d.getDate() === day.getDate()
    );
  };

  useEffect(() => {
    if (!villageDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        villageDropdownRef.current &&
        !villageDropdownRef.current.contains(e.target)
      ) {
        setVillageDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [villageDropdownOpen]);

  const allVillages = useMemo(() => {
    const set = new Set();
    (customers || []).forEach((c) => set.add(normalizeVillage(c?.village)));
    (assignments || []).forEach((a) =>
      set.add(normalizeVillage(a?.customers?.village)),
    );
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [customers, assignments]);

  const filteredVillageOptions = useMemo(() => {
    if (!villageSearch.trim()) return allVillages;
    const q = villageSearch.toLowerCase();
    return allVillages.filter((v) => v.toLowerCase().includes(q));
  }, [allVillages, villageSearch]);

  const toggleVillage = (villageName) => {
    const v = normalizeVillage(villageName);
    setSelectedVillages((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  };

  const selectedVillageLabel =
    selectedVillages.length === 0
      ? "All villages"
      : selectedVillages.length === 1
        ? selectedVillages[0]
        : `${selectedVillages.length} villages`;

  const phaseConfig = {
    survey: {
      role: "surveyor",
      completed: new Set([
        "survey_complete",
        "civil_pending",
        "civil_inprogress",
        "civil_complete",
        "technical_pending",
        "technical_inprogress",
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ]),
      pending: new Set(["survey_not_started", "survey_inprogress"]),
    },
    civil: {
      role: "civil",
      completed: new Set([
        "civil_complete",
        "technical_pending",
        "technical_inprogress",
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ]),
      pending: new Set(["civil_pending", "civil_inprogress"]),
    },
    technical: {
      role: "technical",
      completed: new Set([
        "technical_complete",
        "commissioning_not_started",
        "commissioning_inprogress",
        "commissioning_complete",
      ]),
      pending: new Set(["technical_pending", "technical_inprogress"]),
    },
    commissioning: {
      role: "commissioning",
      completed: new Set(["commissioning_complete"]),
      pending: new Set([
        "commissioning_not_started",
        "commissioning_inprogress",
      ]),
    },
  };

  const surveysByCustomerId = useMemo(() => {
    const map = new Map();
    (surveys || []).forEach((s) => {
      if (s?.customer_id) map.set(s.customer_id, s);
    });
    return map;
  }, [surveys]);

  const feasibleCount = useMemo(() => {
    let n = 0;
    (customers || []).forEach((c) => {
      const s = surveysByCustomerId.get(c.id);
      if (s?.roof_feasible === true) n += 1;
    });
    return n;
  }, [customers, surveysByCustomerId]);

  const nonFeasibleCount = useMemo(() => {
    let n = 0;
    (customers || []).forEach((c) => {
      const s = surveysByCustomerId.get(c.id);
      if (s?.roof_feasible === false) n += 1;
    });
    return n;
  }, [customers, surveysByCustomerId]);

  const notStartedCount = useMemo(() => {
    return (customers || []).filter((c) => c?.status === "survey_not_started")
      .length;
  }, [customers]);

  const villageProgressRows = useMemo(() => {
    const cfg = phaseConfig[phaseTab] || phaseConfig.survey;
    const byVillage = new Map();

    (customers || []).forEach((c) => {
      const village = normalizeVillage(c?.village);
      if (!byVillage.has(village))
        byVillage.set(village, { village, completed: 0, pending: 0 });
      const row = byVillage.get(village);
      const status = c?.status || "";
      if (cfg.completed.has(status)) row.completed += 1;
      else if (cfg.pending.has(status)) row.pending += 1;
    });

    return Array.from(byVillage.values())
      .filter((r) => r.completed + r.pending > 0)
      .sort((a, b) => b.completed + b.pending - (a.completed + a.pending));
  }, [customers, phaseTab]);

  const todayAllocationsRows = useMemo(() => {
    const cfg = phaseConfig[phaseTab] || phaseConfig.survey;
    const today = new Date();
    const byVillage = new Map();

    (assignments || [])
      .filter((a) => a?.users?.role === cfg.role)
      .forEach((a) => {
        if (!isSameDay(a?.assigned_at, today)) return;
        const village = normalizeVillage(a?.customers?.village);
        byVillage.set(village, (byVillage.get(village) || 0) + 1);
      });

    return Array.from(byVillage.entries())
      .map(([village, count]) => ({ village, count }))
      .sort((a, b) => b.count - a.count);
  }, [assignments, phaseTab]);

  const currentAllocationsRows = useMemo(() => {
    const cfg = phaseConfig[phaseTab] || phaseConfig.survey;
    const byVillage = new Map();

    (assignments || [])
      .filter((a) => a?.users?.role === cfg.role)
      .filter((a) => a?.status !== "completed")
      .forEach((a) => {
        const village = normalizeVillage(a?.customers?.village);
        byVillage.set(village, (byVillage.get(village) || 0) + 1);
      });

    return Array.from(byVillage.entries())
      .map(([village, count]) => ({ village, count }))
      .sort((a, b) => b.count - a.count);
  }, [assignments, phaseTab]);

  const villageProgressByName = useMemo(() => {
    return new Map((villageProgressRows || []).map((r) => [r.village, r]));
  }, [villageProgressRows]);

  const displayedVillageProgressRows =
    selectedVillages.length > 0
      ? selectedVillages.map((v) => {
          const row = villageProgressByName.get(v);
          return row || { village: v, completed: 0, pending: 0 };
        })
      : villageProgressRows;

  const todayAllocationsByName = useMemo(() => {
    return new Map(
      (todayAllocationsRows || []).map((r) => [r.village, r.count]),
    );
  }, [todayAllocationsRows]);

  const displayedTodayAllocationsRows =
    selectedVillages.length > 0
      ? selectedVillages.map((v) => ({
          village: v,
          count: todayAllocationsByName.get(v) || 0,
        }))
      : todayAllocationsRows;

  const currentAllocationsByName = useMemo(() => {
    return new Map(
      (currentAllocationsRows || []).map((r) => [r.village, r.count]),
    );
  }, [currentAllocationsRows]);

  const displayedCurrentAllocationsRows =
    selectedVillages.length > 0
      ? selectedVillages.map((v) => ({
          village: v,
          count: currentAllocationsByName.get(v) || 0,
        }))
      : currentAllocationsRows;

  const todayAllocationsToShow =
    selectedVillages.length > 0
      ? displayedTodayAllocationsRows
      : (todayAllocationsRows || []).slice(0, 20);

  const currentAllocationsToShow =
    selectedVillages.length > 0
      ? displayedCurrentAllocationsRows
      : (currentAllocationsRows || []).slice(0, 20);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!cluster) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-center">
              <Icon
                name="AlertCircle"
                size={48}
                className="mx-auto mb-4 text-muted-foreground"
              />
              <h2 className="text-2xl font-semibold text-foreground mb-2">
                No Cluster Assigned
              </h2>
              <p className="text-muted-foreground">
                Please contact an administrator to assign you to a cluster.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cluster Dashboard - {cluster.name}</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
                {cluster.name}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {cluster.projects?.name || "Cluster Management Dashboard"}
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">
                    Total Customers
                  </p>
                  <Icon name="Users" size={20} className="text-primary" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {stats.totalCustomers}
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {stats.pending}
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <Icon name="Activity" size={20} className="text-primary" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {stats.inProgress}
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <Icon
                    name="CheckCircle2"
                    size={20}
                    className="text-success"
                  />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground">
                  {stats.completed}
                </p>
              </div>
            </div>

            {/* View switcher */}
            <div className="bg-card border border-border rounded-lg mb-6">
              <div className="flex border-b border-border">
                <button
                  onClick={() => setActiveTab("village")}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === "village"
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Map" size={18} />
                    <span>Village Analytics</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("teams")}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === "teams"
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="UserCog" size={18} />
                    <span>Teams</span>
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                      {teams?.length || 0}
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("members")}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === "members"
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Users" size={18} />
                    <span>Team Members</span>
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-muted text-xs">
                      {teamMembers?.length || 0}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {activeTab === "village" && (
              <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6 md:mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                      Village Analytics
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Feasible / Non-feasible / Not Started, plus village-wise
                      progress and allocations.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end w-full sm:w-auto">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant={phaseTab === "survey" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPhaseTab("survey")}
                        iconName="ClipboardCheck"
                        iconPosition="left"
                      >
                        Survey
                      </Button>
                      <Button
                        variant={phaseTab === "civil" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPhaseTab("civil")}
                        iconName="HardHat"
                        iconPosition="left"
                      >
                        Civil
                      </Button>
                      <Button
                        variant={
                          phaseTab === "technical" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setPhaseTab("technical")}
                        iconName="Wrench"
                        iconPosition="left"
                      >
                        Technical
                      </Button>
                      <Button
                        variant={
                          phaseTab === "commissioning" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setPhaseTab("commissioning")}
                        iconName="ClipboardCheck"
                        iconPosition="left"
                      >
                        Commissioning
                      </Button>
                    </div>

                    <div
                      className="flex flex-col gap-1 relative w-full sm:w-72"
                      ref={villageDropdownRef}
                    >
                      <label className="text-sm font-medium text-foreground">
                        Villages
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setVillageDropdownOpen((o) => !o);
                          if (!villageDropdownOpen) setVillageSearch("");
                        }}
                        className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                          selectedVillages.length === 0
                            ? "text-muted-foreground"
                            : "text-foreground"
                        }`}
                      >
                        <span className="truncate text-left">
                          {selectedVillageLabel}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 transition-transform ${
                            villageDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {villageDropdownOpen && (
                        <div className="absolute right-0 top-full z-[10000] mt-1 w-full min-w-[240px] max-w-[360px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
                          <div className="p-2 border-b border-border">
                            <input
                              type="search"
                              placeholder="Search villages..."
                              value={villageSearch}
                              onChange={(e) => setVillageSearch(e.target.value)}
                              className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                              autoFocus
                            />
                          </div>
                          <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[220px]">
                            {filteredVillageOptions.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-muted-foreground">
                                No villages match
                              </div>
                            ) : (
                              filteredVillageOptions.map((v) => {
                                const checked = selectedVillages.includes(v);
                                return (
                                  <div
                                    key={v}
                                    role="option"
                                    aria-selected={checked}
                                    className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                    onClick={() => toggleVillage(v)}
                                  >
                                    <span
                                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                        checked
                                          ? "bg-primary border-primary text-primary-foreground"
                                          : "border-input"
                                      }`}
                                    >
                                      {checked ? (
                                        <Check className="h-3 w-3" />
                                      ) : null}
                                    </span>
                                    <span className="truncate">{v}</span>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          <div className="p-2 border-t border-border flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedVillages([])}
                              className="text-sm text-muted-foreground hover:underline"
                            >
                              Clear
                            </button>
                            <span className="text-xs text-muted-foreground">
                              {selectedVillages.length === 0
                                ? "All"
                                : `${selectedVillages.length} selected`}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Feasible sites
                        </p>
                        <p className="text-2xl font-semibold text-foreground">
                          {feasibleCount.toLocaleString()}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon
                          name="CheckCircle2"
                          size={18}
                          className="text-success"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Non-feasible sites
                        </p>
                        <p className="text-2xl font-semibold text-foreground">
                          {nonFeasibleCount.toLocaleString()}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                        <Icon
                          name="XCircle"
                          size={18}
                          className="text-destructive"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Not started
                        </p>
                        <p className="text-2xl font-semibold text-foreground">
                          {notStartedCount.toLocaleString()}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                        <Icon name="Clock" size={18} className="text-warning" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  <div className="rounded-lg border border-border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-foreground">
                        Village progress ({phaseTab})
                      </h3>
                      <span className="text-xs text-muted-foreground">
                        Pending vs Completed
                      </span>
                    </div>
                    <div className="max-h-[520px] overflow-auto">
                      <table className="w-full">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                              Village
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                              Completed
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                              Pending
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {displayedVillageProgressRows.map((r) => (
                            <tr key={r.village}>
                              <td className="px-3 py-2 text-sm text-foreground">
                                {r.village}
                              </td>
                              <td className="px-3 py-2 text-sm text-foreground text-right">
                                {r.completed.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-sm text-foreground text-right">
                                {r.pending.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                          {displayedVillageProgressRows.length === 0 && (
                            <tr>
                              <td
                                colSpan={3}
                                className="px-3 py-6 text-center text-sm text-muted-foreground"
                              >
                                No data.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {selectedVillages.length === 0
                        ? "All villages shown. Scroll to view more."
                        : "Filtered villages shown."}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border p-4 space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-foreground">
                          Today allocation ({phaseTab})
                        </h3>
                        <span className="text-xs text-muted-foreground">
                          Assigned today
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                Village
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                                No. of Units
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {todayAllocationsToShow.map((r) => (
                              <tr key={r.village}>
                                <td className="px-3 py-2 text-sm text-foreground">
                                  {r.village}
                                </td>
                                <td className="px-3 py-2 text-sm text-foreground text-right">
                                  {r.count.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                            {todayAllocationsToShow.length === 0 && (
                              <tr>
                                <td
                                  colSpan={2}
                                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                                >
                                  No allocations today.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-foreground">
                          Current allocations ({phaseTab})
                        </h3>
                        <span className="text-xs text-muted-foreground">
                          Active (not completed)
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                                Village
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {currentAllocationsToShow.map((r) => (
                              <tr key={r.village}>
                                <td className="px-3 py-2 text-sm text-foreground">
                                  {r.village}
                                </td>
                                <td className="px-3 py-2 text-sm text-foreground text-right">
                                  {r.count.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                            {currentAllocationsToShow.length === 0 && (
                              <tr>
                                <td
                                  colSpan={2}
                                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                                >
                                  No active allocations.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === "teams" && (
              <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Teams ({teams.length})
                  </h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => {
                      setIsAddingTeam(true);
                      setEditingTeamId(null);
                      setTeamForm({ id: null, name: "", user_ids: [] });
                      setTeamFormErrors({});
                    }}
                  >
                    Add Team
                  </Button>
                </div>
                {teams.length > 0 ? (
                  <div className="space-y-3">
                    {teams.map((team) => {
                      // Get members directly where team_id matches
                      const membersInTeam =
                        teamMembers?.filter((m) => m.team_id === team.id) || [];
                      const isExpanded = expandedTeams.has(team.id);

                      return (
                        <div
                          key={team.id}
                          className="border border-border rounded-lg overflow-hidden"
                        >
                          <div className="p-4 hover:bg-muted/50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div
                                className="flex items-center gap-3 flex-1 cursor-pointer"
                                onClick={() => {
                                  const newExpanded = new Set(expandedTeams);
                                  if (newExpanded.has(team.id)) {
                                    newExpanded.delete(team.id);
                                  } else {
                                    newExpanded.add(team.id);
                                  }
                                  setExpandedTeams(newExpanded);
                                }}
                              >
                                <Icon
                                  name={
                                    isExpanded ? "ChevronDown" : "ChevronRight"
                                  }
                                  size={16}
                                  className="text-muted-foreground"
                                />
                                <Icon
                                  name="UserCog"
                                  size={20}
                                  className="text-primary"
                                />
                                <div>
                                  <h3 className="font-medium text-foreground">
                                    {team.name}
                                  </h3>
                                  <p className="text-sm text-muted-foreground">
                                    {membersInTeam.length} member
                                    {membersInTeam.length !== 1 ? "s" : ""}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="Edit2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTeam(team);
                                  }}
                                  disabled={deletingTeamId === team.id}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="Trash2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTeam(team.id);
                                  }}
                                  loading={deletingTeamId === team.id}
                                  disabled={deletingTeamId === team.id}
                                  className="text-destructive hover:text-destructive"
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t border-border bg-muted/20 p-4">
                              {membersInTeam.length > 0 ? (
                                <div className="space-y-2">
                                  {membersInTeam.map((member) => (
                                    <div
                                      key={member.id}
                                      className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg"
                                    >
                                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Icon
                                          name="User"
                                          size={16}
                                          className="text-primary"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-foreground">
                                          {member.name || "Unnamed User"}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {member.unique_id || "N/A"} •{" "}
                                          {member.role === "super_admin" ||
                                          member.role === "project_admin"
                                            ? "Admin"
                                            : member.role === "cluster_manager"
                                              ? "Cluster Manager"
                                              : member.role
                                                  ?.charAt(0)
                                                  .toUpperCase() +
                                                  member.role?.slice(1) ||
                                                "Staff"}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                  No members in this team
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon
                      name="UserCog"
                      size={48}
                      className="mx-auto mb-4 text-muted-foreground"
                    />
                    <p className="text-muted-foreground">
                      No teams in this cluster yet. Click "Add Team" to create
                      one.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Team Add/Edit Modal */}
            {(isAddingTeam || editingTeamId) && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
                <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon
                          name="UserCog"
                          size={20}
                          className="text-primary"
                        />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">
                          {editingTeamId ? "Edit Team" : "Add Team"}
                        </h2>
                      </div>
                    </div>
                    <button
                      onClick={handleCancelTeam}
                      className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                      disabled={teamFormLoading}
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>

                  <form onSubmit={handleTeamSubmit} className="p-6 space-y-4">
                    <Input
                      label="Team Name"
                      type="text"
                      placeholder="e.g., Team Alpha, Team Beta"
                      value={teamForm.name}
                      onChange={(e) => {
                        setTeamForm({ ...teamForm, name: e.target.value });
                        if (teamFormErrors?.name)
                          setTeamFormErrors({ ...teamFormErrors, name: "" });
                      }}
                      error={teamFormErrors?.name}
                      required
                    />

                    <Select
                      label="Team Members (Optional)"
                      options={userOptions}
                      value={teamForm.user_ids}
                      onChange={(value) => {
                        setTeamForm({ ...teamForm, user_ids: value || [] });
                      }}
                      multiple
                      searchable
                      placeholder="Select team members (optional)"
                      description="Optionally select one or more staff members to be part of this team."
                    />

                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancelTeam}
                        disabled={teamFormLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="default"
                        loading={teamFormLoading}
                        disabled={teamFormLoading}
                      >
                        {editingTeamId ? "Update" : "Create"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Team Members Tab */}
            {activeTab === "members" && (
              <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Team Members ({teamMembers.length})
                  </h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => {
                      setIsAddingMember(true);
                      setEditingMemberId(null);
                      setMemberForm({ name: "", role: "", team_id: "" });
                      setMemberFormErrors({});
                    }}
                  >
                    Add Member
                  </Button>
                </div>
                {/* Team Filter */}
                <div className="max-w-xs mb-4">
                  <Select
                    label="Filter by Team"
                    options={[
                      { value: "all", label: "All Teams" },
                      ...(teams?.map((team) => ({
                        value: team.id,
                        label: team.name,
                      })) || []),
                    ]}
                    value={selectedTeamFilter}
                    onChange={(value) => setSelectedTeamFilter(value)}
                    searchable
                  />
                </div>
                {teamMembers.length > 0 ? (
                  <div className="space-y-2">
                    {teamMembers
                      .filter((member) => {
                        if (selectedTeamFilter === "all") return true;
                        return member.team_id === selectedTeamFilter;
                      })
                      .map((member) => {
                        const memberTeam = member.team_id
                          ? teams?.find((t) => t.id === member.team_id)
                          : null;

                        return (
                          <div
                            key={member.id}
                            className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Icon
                                    name="User"
                                    size={20}
                                    className="text-primary"
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-foreground">
                                    {member.name || "Unnamed User"}
                                  </p>
                                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                                    <span>{member.unique_id || "N/A"}</span>
                                    {memberTeam && (
                                      <>
                                        <span>•</span>
                                        <span className="flex items-center gap-1">
                                          <Icon name="UserCog" size={12} />
                                          {memberTeam.name}
                                        </span>
                                      </>
                                    )}
                                    {!memberTeam && (
                                      <>
                                        <span>•</span>
                                        <span className="text-muted-foreground">
                                          No Team
                                        </span>
                                      </>
                                    )}
                                    <span>•</span>
                                    <span className="px-2 py-0.5 rounded-lg bg-primary/10 text-primary font-medium">
                                      {member.role === "super_admin" ||
                                      member.role === "project_admin"
                                        ? "Admin"
                                        : member.role === "cluster_manager"
                                          ? "Cluster Manager"
                                          : member.role
                                              ?.charAt(0)
                                              .toUpperCase() +
                                              member.role?.slice(1) || "Staff"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="Edit2"
                                  onClick={() => handleEditMember(member)}
                                  disabled={deletingMemberId === member.id}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconName="Trash2"
                                  onClick={() => handleDeleteMember(member.id)}
                                  loading={deletingMemberId === member.id}
                                  disabled={deletingMemberId === member.id}
                                  className="text-destructive hover:text-destructive"
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon
                      name="Users"
                      size={48}
                      className="mx-auto mb-4 text-muted-foreground"
                    />
                    <p className="text-muted-foreground">
                      No team members in this cluster yet. Click "Add Member" to
                      create one.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Password Display Modal */}
            {(generatedPassword || newUserUniqueId) && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
                <div className="bg-card rounded-lg shadow-xl w-full max-w-md">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Member Created Successfully
                    </h3>
                    <div className="space-y-3">
                      {newUserUniqueId && (
                        <div>
                          <label className="text-sm font-medium text-foreground">
                            User ID
                          </label>
                          <div className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm">
                            {newUserUniqueId}
                          </div>
                        </div>
                      )}
                      {generatedPassword && (
                        <div>
                          <label className="text-sm font-medium text-foreground">
                            Password
                          </label>
                          <div className="mt-1 p-3 bg-muted rounded-lg font-mono text-sm">
                            {generatedPassword}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Please save this password. It will not be shown
                            again.
                          </p>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="default"
                      className="w-full mt-6"
                      onClick={handleClearPassword}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Member Add/Edit Modal */}
            {(isAddingMember || editingMemberId) && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
                <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">
                          {editingMemberId
                            ? "Edit Team Member"
                            : "Add Team Member"}
                        </h2>
                      </div>
                    </div>
                    <button
                      onClick={handleCancelMember}
                      className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                      disabled={memberFormLoading}
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>

                  <form onSubmit={handleMemberSubmit} className="p-6 space-y-4">
                    <Input
                      label="Name"
                      type="text"
                      placeholder="Enter member name"
                      value={memberForm.name}
                      onChange={(e) => {
                        setMemberForm({ ...memberForm, name: e.target.value });
                        if (memberFormErrors?.name)
                          setMemberFormErrors({
                            ...memberFormErrors,
                            name: "",
                          });
                      }}
                      error={memberFormErrors?.name}
                      required
                    />

                    <Select
                      label="Role"
                      options={roleOptions}
                      value={memberForm.role}
                      onChange={(value) => {
                        setMemberForm({ ...memberForm, role: value });
                        if (memberFormErrors?.role)
                          setMemberFormErrors({
                            ...memberFormErrors,
                            role: "",
                          });
                      }}
                      error={memberFormErrors?.role}
                      placeholder="Select role"
                      required
                    />

                    <Select
                      label="Team"
                      options={[
                        { value: "", label: "— No Team —" },
                        ...teamOptions,
                      ]}
                      value={memberForm.team_id || ""}
                      onChange={(value) => {
                        setMemberForm({ ...memberForm, team_id: value || "" });
                      }}
                      placeholder="Select team (optional)"
                      searchable
                    />

                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancelMember}
                        disabled={memberFormLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="default"
                        loading={memberFormLoading}
                        disabled={memberFormLoading}
                      >
                        {editingMemberId ? "Update" : "Create"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default ClusterDashboard;
